import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import moment from 'moment';
import {toast} from 'react-toastify';
import {
  GET_DABAPRO_GROWTH_STATISTICS,
  GET_USER_GROWTH_STATISTICS,
} from '../resolvers';
import {Section} from '../../shared/DashboardUI';
import GrowthChart from '../../shared/GrowthChart';
import MonthNavigator from '../../shared/MonthNavigator';
import styles from '../../shared/dashboard.module.scss';

/**
 * Signups and Daba Pro subscriptions, by day, for one month.
 *
 * The rewrite fixed a bug worth recording. Stepping back a month called
 * `refetch` — the *user growth* query — and then read
 * `stats?.dabaProStatistics?.daily` off the result. That query never selects
 * `dabaProStatistics`, so on the Daba Pro chart the field was always undefined
 * and the chart silently emptied. Each measure now owns its own query and its
 * own month, so neither can answer for the other.
 *
 * The previous month is fetched alongside the current one, which is what lets
 * the summary say whether this month is up or down rather than leaving the
 * reader to remember what last month looked like.
 */

const GRAPHS = [
  {value: 'USERS', label: 'Users', seriesIndex: 0, measure: 'Signups'},
  {
    value: 'DABAPRO',
    label: 'Daba Pro',
    seriesIndex: 5,
    measure: 'Subscriptions',
  },
];

const previousMonthOf = date => date.clone().subtract(1, 'month');

const toPoints = daily =>
  (daily ?? []).map(item => ({date: item?.createdAt, amount: item?.total}));

export const UserGrowthTab = () => {
  const [activeGraph, setActiveGraph] = useState('USERS');
  const [month, setMonth] = useState(() => moment());

  const graph = GRAPHS.find(item => item.value === activeGraph) ?? GRAPHS[0];
  const isUsers = activeGraph === 'USERS';
  const previous = useMemo(() => previousMonthOf(month), [month]);

  const currentVariables = {month: month.month() + 1, year: month.year()};
  const previousVariables = {
    month: previous.month() + 1,
    year: previous.year(),
  };

  const {data: userData, loading: userLoading} = useQuery(
    GET_USER_GROWTH_STATISTICS,
    {
      variables: currentVariables,
      skip: !isUsers,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      onError: () => toast.error('Could not load the users growth chart'),
    },
  );

  const {data: userPrevious, loading: userPreviousLoading} = useQuery(
    GET_USER_GROWTH_STATISTICS,
    {
      variables: previousVariables,
      skip: !isUsers,
      errorPolicy: 'all',
    },
  );

  const {data: proData, loading: proLoading} = useQuery(
    GET_DABAPRO_GROWTH_STATISTICS,
    {
      variables: currentVariables,
      skip: isUsers,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      onError: () => toast.error('Could not load the Daba Pro growth chart'),
    },
  );

  const {data: proPrevious, loading: proPreviousLoading} = useQuery(
    GET_DABAPRO_GROWTH_STATISTICS,
    {
      variables: previousVariables,
      skip: isUsers,
      errorPolicy: 'all',
    },
  );

  const daily = isUsers
    ? userData?.getUserGrowthStatistics?.data?.userStatistics?.daily
    : proData?.getDabaProGrowthStatistics?.data?.dabaProStatistics?.daily;

  const previousDaily = isUsers
    ? userPrevious?.getUserGrowthStatistics?.data?.userStatistics?.daily
    : proPrevious?.getDabaProGrowthStatistics?.data?.dabaProStatistics?.daily;

  const loading = isUsers ? userLoading : proLoading;

  return (
    <div className={styles.section}>
      <Section
        title={`${graph.measure} per day`}
        description={`${graph.label} added on each day of ${month.format(
          'MMMM YYYY',
        )}. Days with none are shown as zero.`}
        actions={
          <div className={styles.growthControls}>
            <div className={styles.segmented} role="group" aria-label="Measure">
              {GRAPHS.map(item => (
                <button
                  key={item.value}
                  type="button"
                  className={`${styles.segment} ${
                    activeGraph === item.value ? styles.segmentActive : ''
                  }`}
                  onClick={() => setActiveGraph(item.value)}>
                  {item.label}
                </button>
              ))}
            </div>
            <MonthNavigator
              value={month}
              onChange={setMonth}
              disabled={loading}
            />
          </div>
        }>
        <GrowthChart
          points={toPoints(daily)}
          month={month.month() + 1}
          year={month.year()}
          seriesIndex={graph.seriesIndex}
          label={graph.label}
          previousPoints={previousDaily ? toPoints(previousDaily) : null}
          previousLoading={isUsers ? userPreviousLoading : proPreviousLoading}
          loading={loading}
        />
      </Section>
    </div>
  );
};

export default UserGrowthTab;
